// Lib
import * as THREE from 'three/webgpu';
import { NodeMaterial } from 'three/webgpu';
import {
  attribute,
  Discard,
  float,
  Fn,
  If,
  instanceIndex,
  length,
  mix,
  positionLocal,
  smoothstep,
  storage,
  uniform,
  uv,
  vec2,
  vec3,
  vec4,
  wgslFn,
} from 'three/tsl';

// Shader
import particleUpdateShader from './shaders/particleUpdate.wgsl';

// Helper
import { MouseHandler } from './helper/MouseHandler';

// --- Parameters ---
const IS_MOBILE = window.matchMedia('(max-width: 767px)').matches;
const NUM_PARTICLES = IS_MOBILE ? 5000 : 10000;
const PARTICLE_SIZE = 3;
const COLOR_RED = vec3(1.0, 0.1, 0.1);
const COLOR_BLUE = vec3(0.2, 0.2, 0.8);
const SPEED_FACTOR_STEP = 100.0;


export class App {
  private mouseHandler: MouseHandler;
  private renderer: THREE.WebGPURenderer;
  private computeNode?: THREE.ComputeNode;
  private scene: THREE.Scene;
  private camera: THREE.OrthographicCamera;
  private canvas: HTMLCanvasElement;
  private width: number;
  private height: number;
  private pixelRatio: number = 1;
  private widthUniform = uniform(0, 'float');
  private heightUniform = uniform(0, 'float');
  constructor(canvas: HTMLCanvasElement) {
    // Setup Canvas
    this.canvas = canvas;
    this.width = this.canvas.clientWidth;
    this.height = this.canvas.clientHeight;
    this.pixelRatio = Math.min(window.devicePixelRatio || 1, 2);

    // Helper
    this.mouseHandler = new MouseHandler(this.canvas);

    // Setup ThreeJS
    this.scene = new THREE.Scene();
    this.camera = new THREE.OrthographicCamera(0, this.width, this.height, 0, 0.1, 100);
    this.camera.position.z = 1;

    this.renderer = new THREE.WebGPURenderer({
      antialias: true,
      canvas: this.canvas,
    });

    this.init();
    window.addEventListener('resize', this.onResize);
  }

  private async init() {
    if (!this.renderer) return;

    await this.renderer.init();
    this.renderer.setPixelRatio(this.pixelRatio);
    this.renderer.setSize(this.width, this.height, false);
    this.renderer.setClearColor(0x111111, 1);
    this.widthUniform.value = this.width;
    this.heightUniform.value = this.height;

    // --- データ準備 ---
    // 位置と速度を格納するバッファ
    const baseData = new Float32Array(NUM_PARTICLES * 4);
    for (let i = 0; i < NUM_PARTICLES; i++) {
      baseData[i * 4 + 0] = Math.random() * this.width;      // pos x
      baseData[i * 4 + 1] = Math.random() * this.height;     // pos y
      baseData[i * 4 + 2] = 0; // vel x
      baseData[i * 4 + 3] = 0; // vel y
    }

    // Compute Shader用のStorage Bufferを作成
    const particleBuffer = new THREE.StorageInstancedBufferAttribute(baseData, 4);

    // TSLでバッファへのアクセスを定義
    const particles = storage(particleBuffer, 'vec4', NUM_PARTICLES);

    const densityData = new Float32Array(NUM_PARTICLES); // 空の配列
    const densityStorageAttr = new THREE.StorageInstancedBufferAttribute(densityData, 1);
    const densityStorage = storage(densityStorageAttr, 'float', NUM_PARTICLES);

    // --- Compute Shader (TSL + WGSL) ---
    const computeUpdate = wgslFn(particleUpdateShader);

    // Compute Nodeの定義
    // instanceIndex は現在処理中のパーティクルID
    this.computeNode = computeUpdate(
      particles,
      densityStorage,
      instanceIndex,
      uniform(NUM_PARTICLES, 'uint'),
      this.widthUniform,
      this.heightUniform,
      this.mouseHandler.getMouseUniform(),
      uniform(0.016, 'float')
    ).compute(NUM_PARTICLES); // Workgroup size等の調整は自動/あるいは指定可

    // --- 描画 (Render) ---
    // パーティクルを正方形のビルボードとして描画
    const geometry = new THREE.PlaneGeometry(1, 1);
    geometry.setAttribute('particleData', particleBuffer);
    geometry.setAttribute('densityData', densityStorageAttr);

    const material = new NodeMaterial();

    // Fragment Shaderで円形マスクを適用
    // uv()は0から1の範囲なので、-0.5から0.5の範囲に変換してから距離を計算
    material.fragmentNode = Fn(() => {
      // 円形処理
      const localPos = uv().sub(vec2(0.5, 0.5));
      const distFromCenter = length(localPos);
      const radius = float(0.5);
      If(distFromCenter.greaterThan(radius), () => {
        Discard();
      });

      const densityAttribute = attribute('densityData', 'float');
      const densityColor = mix(COLOR_BLUE, COLOR_RED, densityAttribute.div(50.0).clamp(0, 1));

      // 色の設定
      return vec4(densityColor, 1);
    })();

    // Vertex Shaderでの位置更新
    // TSLを使って、particleDataバッファから位置を取り出し、頂点位置に適用
    const particlePos = attribute('particleData', 'vec4').xy;
    material.positionNode = vec3(particlePos, 0).add(vec3(positionLocal.x.mul(PARTICLE_SIZE), positionLocal.y.mul(PARTICLE_SIZE), 0));

    const mesh = new THREE.InstancedMesh(geometry, material, NUM_PARTICLES);
    mesh.frustumCulled = false;
    this.scene.add(mesh);

    this.renderer.setAnimationLoop(() => {
      this.animate();
    });
  }

  private animate = () => {
    this.renderer.compute(this.computeNode as THREE.ComputeNode);
    this.renderer.render(this.scene, this.camera);
  }

  private onResize = () => {
    this.width = this.canvas.clientWidth;
    this.height = this.canvas.clientHeight;
    const nextPixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    if (nextPixelRatio !== this.pixelRatio) {
      this.pixelRatio = nextPixelRatio;
      this.renderer.setPixelRatio(this.pixelRatio);
    }
    this.renderer.setSize(this.width, this.height, false);
    this.camera.right = this.width;
    this.camera.top = this.height;
    this.camera.updateProjectionMatrix();
    this.widthUniform.value = this.width;
    this.heightUniform.value = this.height;
  }

}
